import type {
  GlossaryEntry,
  GlossaryIssue,
  GlossaryStats,
  IngestJob,
  IngestJobSummary,
  ProfileId,
  ReviewDecision,
  StoredReport,
} from '@cixing/shared';

/**
 * 管理画面専用の API クライアント。
 * 一般画面(api.ts)と分けてあるのは、通常利用のバンドルへ管理機能の型や
 * エンドポイント知識を混ぜないため。
 */

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // Cookie(管理者セッション)を送るために必須。
    credentials: 'same-origin',
    headers: {
      // FormData のときは Content-Type をブラウザに任せる(boundary を壊さない)。
      ...(init?.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let message = '通信に失敗しました。';
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // JSON でないエラー応答は既定メッセージのまま。
    }
    throw new Error(message);
  }

  return (await response.json()) as T;
}

export function adminLogin(code: string): Promise<{ ok: boolean }> {
  return request('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ code, scope: 'admin' }),
  });
}

// ---- 用語集 ---------------------------------------------------------------

export interface GlossaryIssuesResponse {
  version: string;
  loadedAt: string;
  stats: GlossaryStats;
  errors: GlossaryIssue[];
  warnings: GlossaryIssue[];
}

export function fetchGlossaryIssues(): Promise<GlossaryIssuesResponse> {
  return request('/api/admin/glossary/issues');
}

export function fetchGlossaryEntries(
  q: string,
  profile: ProfileId | '',
): Promise<{ total: number; entries: GlossaryEntry[] }> {
  const params = new URLSearchParams();
  if (q !== '') params.set('q', q);
  if (profile !== '') params.set('profile', profile);
  const qs = params.toString();
  return request(`/api/admin/glossary/entries${qs ? `?${qs}` : ''}`);
}

export function reloadGlossary(): Promise<{
  version: string;
  stats: GlossaryStats;
  errorCount: number;
}> {
  return request('/api/admin/glossary/reload', { method: 'POST' });
}

// ---- 資料取込 -------------------------------------------------------------

export function uploadIngestFile(file: File): Promise<{ jobId: string }> {
  const form = new FormData();
  form.append('file', file);
  return request('/api/admin/ingest', { method: 'POST', body: form });
}

export function fetchIngestJobs(): Promise<{ jobs: IngestJobSummary[] }> {
  return request('/api/admin/ingest');
}

export function fetchIngestJob(jobId: string): Promise<{ job: IngestJob }> {
  return request(`/api/admin/ingest/${encodeURIComponent(jobId)}`);
}

export function patchCandidate(
  jobId: string,
  candidateId: string,
  patch: { decision?: ReviewDecision; targetJa?: string; profile?: ProfileId },
): Promise<{ job: IngestJob }> {
  return request(
    `/api/admin/ingest/${encodeURIComponent(jobId)}/candidates/${encodeURIComponent(candidateId)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
}

export function applyIngestJob(
  jobId: string,
): Promise<{ job: IngestJob; appliedCsvPath: string }> {
  return request(`/api/admin/ingest/${encodeURIComponent(jobId)}/apply`, { method: 'POST' });
}

// ---- 誤訳レポート ---------------------------------------------------------

export function fetchReports(): Promise<{ total: number; reports: StoredReport[] }> {
  return request('/api/admin/reports');
}

export function reportToGlossary(body: {
  sourceZh: string;
  targetJa: string;
  profile: ProfileId;
}): Promise<{ ok: boolean; total: number }> {
  return request('/api/admin/reports/to-glossary', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
